import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spin } from "antd";
import { Copy, Eye, EyeOff, Smartphone } from "lucide-react";
import { AN_COLORS, AN_MONO, anAlpha } from "../../design-system/tokens";
import { fmtDate } from "../../design-system/tableUi";
import copyText from "../../utils/copyText";
import { getPasswordRevealResult, requestPasswordReveal } from "../../service/dbVaultService";
import {
  REVEAL_DEFAULT_SHOW_SECONDS,
  REVEAL_POLL_MS,
  REVEAL_TIMEOUT_MS,
  countdownText,
  revealFailure,
} from "./dbVaultModel";

// Reveal a managed database password once (POST users/password/reveal, then
// poll reveal/result). The approval push goes to the admin's own phone.
//
// The password lives in component state only while it is on screen: it is
// cleared when the countdown ends, when the dialog closes and on unmount. It
// is never written to storage or logged.
//
// phase: requesting | pending | approved | hidden | denied | expired | shown | unavailable | error
const MASK = "•".repeat(16);

const Note = ({ tone = "neutral", children }) => {
  const fg = tone === "danger" ? AN_COLORS.danger : tone === "warn" ? AN_COLORS.warn : AN_COLORS.ink3;
  return (
    <div
      style={{
        padding: "0.75rem 0.875rem",
        borderRadius: "0.5rem",
        border: `1px solid ${tone === "neutral" ? AN_COLORS.line : anAlpha(fg, 35)}`,
        background: tone === "neutral" ? AN_COLORS.tint : anAlpha(fg, 8),
        color: tone === "neutral" ? AN_COLORS.ink2 : fg,
        fontSize: "0.8125rem",
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
};

const RevealPasswordDialog = ({ open, hostId, role, host, onClose }) => {
  const [phase, setPhase] = useState("requesting");
  const [message, setMessage] = useState("");
  const [secret, setSecret] = useState(null);
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [rerotateAt, setRerotateAt] = useState(null);
  const [copied, setCopied] = useState(false);

  // Bumped on every new request and on close: a poll or timer from an older
  // run sees a different id and stops.
  const runRef = useRef(0);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timerRef.current = null;
    countdownRef.current = null;
  };

  const wipe = useCallback(() => {
    setSecret(null);
    setVisible(false);
    setCopied(false);
  }, []);

  const startCountdown = (run, seconds) => {
    setSecondsLeft(seconds);
    const endsAt = Date.now() + seconds * 1000;
    countdownRef.current = setInterval(() => {
      if (runRef.current !== run) return;
      const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        wipe();
        setPhase("hidden");
      }
    }, 250);
  };

  const poll = (run, revealId, deadline) => {
    timerRef.current = setTimeout(async () => {
      if (runRef.current !== run) return;
      if (Date.now() > deadline) {
        setPhase("expired");
        return;
      }
      let res;
      try {
        res = await getPasswordRevealResult(revealId);
      } catch (err) {
        if (runRef.current !== run) return;
        const f = revealFailure(err?.response?.status);
        setPhase(f.kind);
        setMessage(f.text);
        return;
      }
      if (runRef.current !== run) return;
      const status = String(res?.status || "").toLowerCase();
      if (status === "pending" || !status) {
        poll(run, revealId, deadline);
      } else if (status === "approved" && res?.password) {
        const seconds = Number(res.showSeconds) > 0 ? Number(res.showSeconds) : REVEAL_DEFAULT_SHOW_SECONDS;
        setSecret(res.password);
        setRerotateAt(res.rerotateAt || null);
        setPhase("approved");
        startCountdown(run, seconds);
      } else if (status === "denied" || status === "expired" || status === "shown") {
        setPhase(status);
      } else {
        setPhase("error");
        setMessage("AuthNull sent an answer this console doesn't understand.");
      }
    }, REVEAL_POLL_MS);
  };

  const start = async () => {
    clearTimers();
    wipe();
    const run = ++runRef.current;
    setPhase("requesting");
    setMessage("");
    setRerotateAt(null);
    try {
      const res = await requestPasswordReveal({ hostId, role });
      if (runRef.current !== run) return;
      if (!res?.revealId) {
        setPhase("error");
        setMessage("AuthNull didn't start the reveal. Try again.");
        return;
      }
      setPhase("pending");
      poll(run, res.revealId, Date.now() + REVEAL_TIMEOUT_MS);
    } catch (err) {
      if (runRef.current !== run) return;
      const f = revealFailure(err?.response?.status);
      setPhase(f.kind);
      setMessage(f.text);
    }
  };

  useEffect(() => {
    if (open) start();
    return () => {
      runRef.current += 1;
      clearTimers();
      wipe();
    };
    // Start once per open, for this role.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostId, role]);

  const close = () => {
    runRef.current += 1;
    clearTimers();
    wipe();
    onClose && onClose();
  };

  const onCopy = () => {
    if (!secret) return;
    copyText(secret)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const retry = ["denied", "expired", "shown", "error"].includes(phase);

  const footer = [
    retry && (
      <Button key="retry" onClick={start}>
        Try again
      </Button>
    ),
    <Button key="close" type={retry || phase === "pending" || phase === "requesting" ? "default" : "primary"} onClick={close}>
      {phase === "pending" || phase === "requesting" ? "Cancel" : "Close"}
    </Button>,
  ].filter(Boolean);

  const where = host ? ` on ${host}` : "";

  let body;
  if (phase === "requesting" || phase === "pending") {
    body = (
      <div style={{ display: "flex", alignItems: "center", gap: "0.875rem", padding: "0.5rem 0" }}>
        <div
          style={{
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "50%",
            background: AN_COLORS.primaryL,
            color: AN_COLORS.primary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Smartphone size={18} />
        </div>
        <div style={{ flex: 1, color: AN_COLORS.ink2, fontSize: "0.875rem", lineHeight: 1.5 }}>
          {phase === "requesting" ? "Sending the approval request…" : `Approve the request on your phone to reveal ${role}'s password.`}
        </div>
        <Spin size="small" />
      </div>
    );
  } else if (phase === "approved") {
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <div style={{ color: AN_COLORS.ink2, fontSize: "0.875rem" }}>
          {role}'s password{where}. It's shown once.
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 0.5rem 0.5rem 0.75rem",
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: "0.5rem",
            background: AN_COLORS.tint,
          }}
        >
          <code
            aria-label={visible ? "Password" : "Password, hidden"}
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: AN_MONO,
              fontSize: "0.875rem",
              color: AN_COLORS.ink,
              wordBreak: "break-all",
              userSelect: visible ? "all" : "none",
            }}
          >
            {visible ? secret : MASK}
          </code>
          <Button
            size="small"
            icon={visible ? <EyeOff size={13} /> : <Eye size={13} />}
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? "Hide" : "Show"}
          </Button>
          <Button size="small" icon={<Copy size={13} />} onClick={onCopy}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <div style={{ color: AN_COLORS.muted, fontSize: "0.8125rem" }} aria-live="polite">
          Hidden in <span style={{ fontFamily: AN_MONO, color: AN_COLORS.ink2 }}>{countdownText(secondsLeft)}</span>
        </div>
      </div>
    );
  } else if (phase === "hidden") {
    body = <Note>The password is hidden now. Each reveal shows it once.</Note>;
  } else if (phase === "denied") {
    body = <Note tone="danger">The request was denied.</Note>;
  } else if (phase === "expired") {
    body = <Note tone="warn">The request expired.</Note>;
  } else if (phase === "shown") {
    body = <Note tone="warn">This password was already shown once. Request a new reveal to see it again.</Note>;
  } else {
    body = <Note tone={phase === "unavailable" ? "neutral" : "danger"}>{message}</Note>;
  }

  const showFooterNote = phase === "approved" || phase === "hidden";

  return (
    <Modal
      open={open}
      title={`Reveal ${role}'s password`}
      onCancel={close}
      maskClosable={false}
      destroyOnHidden
      footer={footer}
      width={520}
    >
      {body}
      {showFooterNote && (
        <div style={{ marginTop: "1rem", color: AN_COLORS.muted, fontSize: "0.75rem", lineHeight: 1.5 }}>
          {rerotateAt
            ? `This password will be rotated automatically at ${fmtDate(rerotateAt)}. `
            : "This password will be rotated automatically. "}
          This reveal is recorded in the audit log.
        </div>
      )}
    </Modal>
  );
};

export default RevealPasswordDialog;
